<script lang="ts">
  /**
   * The wet-hands swipe deck (SWIPE-01 / LABEL-03 / D-07/D-08/D-10/D-15).
   *
   * Presents the REVIEW-tier laps (the `reviewLapIndices` side-channel from
   * deriveWorkout, 07-01) ONE card at a time. Right swipe = keep, left = exclude,
   * tap = relabel palette. On commit it OPTIMISTICALLY updates the in-memory
   * `labels` array and re-derives IMMEDIATELY (so SWOLF snaps back live — D-15),
   * then fire-and-forgets `bridge.setLapLabels` (mirrors haptics.ts:32). A ~4s
   * full-width Undo bar restores the PRIOR persisted label (may be an auto-exclude
   * — D-04), never a blanket include. Zero review laps → "All clear" (D-10).
   *
   * SEPARATE view from the ECharts timeline (D-07) — no gesture conflict.
   */
  import type { Lap } from "./laps";
  import type { LapLabel } from "@contract/types";
  import type { CorrectionKey } from "./correctionKey";
  import { haptic } from "./haptics";
  import { get } from "svelte/store";
  import { currentLabels, saveStatus } from "./state";
  import { commitLabelChange } from "./liveCorrection";
  import StrokePill from "./StrokePill.svelte";
  import ReviewCard from "./ReviewCard.svelte";
  import {
    priorLabelFor,
    upsertLabel,
    mergeOntoPrior,
    restorePriorLabel,
    recalledCustomSets,
  } from "./swipeDeckLogic";

  interface Props {
    /** All laps (continuous; deck filters to review-tier). */
    laps: Lap[];
    /** 1-based review-tier lap indices from deriveWorkout (07-01 side-channel). */
    reviewLapIndices: number[];
    /** The flagged-metric line per review lap, e.g. "SWOLF 140 · 2SD slow". */
    flagLineFor: (lap: Lap) => string;
    /** The live persisted-labels array (bindable) — mutating it re-derives (D-15). */
    labels: LapLabel[];
    /** Called after each optimistic label change so the parent re-derives + persists. */
    onLabelsChange: (next: LapLabel[]) => void;
  }
  let {
    laps,
    reviewLapIndices,
    flagLineFor,
    labels,
    onLabelsChange,
  }: Props = $props();

  // Snapshot the review queue ONCE per swim load. We advance an index through it
  // rather than recomputing from reviewLapIndices — a committed card must leave
  // the deck even though its lap stays in `laps` (D-16: never delete the lap).
  let queue = $derived(
    reviewLapIndices
      .map((idx) => laps.find((l) => l.index === idx))
      .filter((l): l is Lap => l != null),
  );
  let cursor = $state(0);
  let current = $derived(cursor < queue.length ? queue[cursor] : null);

  // Drill-label palette (D-11) — fixed seed of big tappable chips. Known HK
  // strokes write a strokeOverride; drill labels write a customName.
  const STROKE_CHIPS = ["Freestyle", "Backstroke", "Breaststroke", "Butterfly", "Mixed"];
  const DRILL_CHIPS = ["Kick", "Pull", "Drill", "Scull", "IM", "Sprint"];

  let paletteOpen = $state(false);
  let customQuery = $state("");
  let recalled = $derived(recalledCustomSets(labels));
  let filteredRecall = $derived(
    customQuery.trim() === ""
      ? recalled
      : recalled.filter((n) => n.toLowerCase().includes(customQuery.trim().toLowerCase())),
  );

  // Undo bar state (07-RESEARCH Pattern 2). `prev` is captured at commit time so
  // the restore re-applies the prior persisted label, not a blanket include (D-04).
  let pending = $state<{ lapIndex: number; key: CorrectionKey | null; prev: LapLabel | null } | null>(null);
  let undoTimer: ReturnType<typeof setTimeout> | undefined;

  // Cleanup so a stray timer can't fire into a torn-down component.
  $effect(() => () => clearTimeout(undoTimer));

  /** Optimistic re-derive + status-tracked persist (the D-15 path). */
  function applyAndPersist(next: LapLabel[]) {
    onLabelsChange(next); // parent reassigns labels → deriveWorkout re-runs LIVE
    commitLabelChange(next); // single status-tracked write path (T5b)
  }

  function showUndo(lapIndex: number, key: CorrectionKey | null, prev: LapLabel | null) {
    clearTimeout(undoTimer); // cancel-on-new-action
    pending = { lapIndex, key, prev };
    haptic("success"); // D-08 — NOT 'notification' (invalid style, PATTERNS correction #1)
    undoTimer = setTimeout(() => {
      pending = null;
    }, 4000);
  }

  function commit(lap: Lap, dir: "keep" | "exclude") {
    const authoritative = get(currentLabels);
    const prev = priorLabelFor(authoritative, lap.index, lap.correctionKey);
    const next: LapLabel = {
      lapIndex: lap.index,
      kind: "outlier",
      excludedFromAggregates: dir === "exclude",
      // T3b write path: stamp composite key for durable identity across re-fetches.
      offsetSeconds: lap.correctionKey.offsetSeconds,
      durationSeconds: lap.correctionKey.durationSeconds,
      duplicateOrdinal: lap.correctionKey.duplicateOrdinal,
    };
    applyAndPersist(upsertLabel(authoritative, mergeOntoPrior(prev, next)));
    showUndo(lap.index, lap.correctionKey, prev);
    advance();
  }

  function applyStroke(lap: Lap, stroke: string) {
    const authoritative = get(currentLabels);
    const prev = priorLabelFor(authoritative, lap.index, lap.correctionKey);
    const existing = prev?.excludedFromAggregates ?? lap.effectiveExcluded;
    const next: LapLabel = {
      lapIndex: lap.index,
      kind: "stroke-correction",
      strokeOverride: stroke,
      excludedFromAggregates: existing,
      // T3b write path: stamp composite key for durable identity across re-fetches.
      offsetSeconds: lap.correctionKey.offsetSeconds,
      durationSeconds: lap.correctionKey.durationSeconds,
      duplicateOrdinal: lap.correctionKey.duplicateOrdinal,
      // This is a stroke decision (2026-07-05 provenance completeness) — the
      // deck's relabel palette had no provenance before this, an untagged gap
      // vs. the SetDetailSheet fix-tile path.
      provenance: "user-corrected",
      createdAtMs: Date.now(),
    };
    applyAndPersist(upsertLabel(authoritative, mergeOntoPrior(prev, next)));
    showUndo(lap.index, lap.correctionKey, prev);
    closePalette();
    advance();
  }

  function applyCustom(lap: Lap, name: string) {
    const authoritative = get(currentLabels);
    const prev = priorLabelFor(authoritative, lap.index, lap.correctionKey);
    const existing = prev?.excludedFromAggregates ?? lap.effectiveExcluded;
    const next: LapLabel = {
      lapIndex: lap.index,
      kind: "drill",
      customName: name,
      excludedFromAggregates: existing,
      // T3b write path: stamp composite key for durable identity across re-fetches.
      offsetSeconds: lap.correctionKey.offsetSeconds,
      durationSeconds: lap.correctionKey.durationSeconds,
      duplicateOrdinal: lap.correctionKey.duplicateOrdinal,
      // A custom-name set is a stroke/vocabulary decision same as applyStroke —
      // NOT stamped on commit()'s pure keep/exclude (an exclusion is not one).
      provenance: "user-corrected",
      createdAtMs: Date.now(),
    };
    applyAndPersist(upsertLabel(authoritative, mergeOntoPrior(prev, next)));
    showUndo(lap.index, lap.correctionKey, prev);
    closePalette();
    advance();
  }

  function undo() {
    clearTimeout(undoTimer);
    if (pending) {
      const authoritative = get(currentLabels);
      applyAndPersist(restorePriorLabel(authoritative, pending.lapIndex, pending.prev, pending.key));
      // Step the cursor back so the undone card returns to the deck.
      if (cursor > 0) cursor -= 1;
    }
    pending = null;
  }

  function advance() {
    cursor += 1;
  }
  function openPalette() {
    paletteOpen = true;
    customQuery = "";
  }
  function closePalette() {
    paletteOpen = false;
  }
</script>

<section class="deck" aria-label="Review laps">
  {#if queue.length === 0}
    <!-- D-10 / SWIPE-01: 0 review laps → "All clear", NO zero-card shell. -->
    <div class="empty">
      <h3 class="t-title">All clear</h3>
      <p class="t-body muted">No laps need review on this swim. Your splits are honest.</p>
    </div>
  {:else if current}
    <header class="deck-head">
      <span class="t-caption muted">Review · {cursor + 1} of {queue.length}</span>
    </header>

    {#key current.index}
      <ReviewCard
        lap={current}
        flagLine={flagLineFor(current)}
        onCommit={(dir) => commit(current, dir)}
        onRelabel={openPalette}
      />
    {/key}

    {#if $saveStatus === 'failed'}
      <div class="persist-error t-caption" role="status">
        Couldn't save that label. It'll re-apply when you reconnect.
      </div>
    {/if}
  {:else}
    <!-- All review cards committed this session — calm done state, not a shell. -->
    <div class="empty">
      <h3 class="t-title">All reviewed</h3>
      <p class="t-body muted">You've cleared every flagged lap on this swim.</p>
    </div>
  {/if}

  {#if pending}
    <button class="undo-bar" onclick={undo}>Undo</button>
  {/if}
</section>

{#if paletteOpen && current}
  <div
    class="palette-scrim"
    role="button"
    tabindex="0"
    aria-label="Close relabel palette"
    onclick={closePalette}
    onkeydown={(e) => e.key === "Escape" && closePalette()}
  ></div>
  <div class="palette" role="dialog" aria-label="Relabel lap">
    <header class="palette-head">
      <span class="t-subtitle">Relabel L{current.index}</span>
      <button class="palette-close" onclick={closePalette} aria-label="Close">✕</button>
    </header>

    <div class="chip-grid">
      {#each STROKE_CHIPS as stroke (stroke)}
        <button class="chip" onclick={() => applyStroke(current, stroke)}>
          <StrokePill {stroke} />
        </button>
      {/each}
    </div>
    <div class="chip-grid">
      {#each DRILL_CHIPS as drill (drill)}
        <button class="chip" onclick={() => applyCustom(current, drill)}>
          <StrokePill stroke={drill} />
        </button>
      {/each}
    </div>

    <div class="recall">
      <input
        class="recall-input"
        type="text"
        placeholder="Your sets"
        bind:value={customQuery}
        aria-label="Recall a named set"
      />
      {#if filteredRecall.length > 0}
        <div class="recall-list">
          {#each filteredRecall as name (name)}
            <button class="recall-item" onclick={() => applyCustom(current, name)}>{name}</button>
          {/each}
        </div>
      {/if}
    </div>
  </div>
{/if}

<style>
  .deck {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    margin: var(--space-4) 0;
  }
  .deck-head {
    display: flex;
    justify-content: flex-start;
  }
  .muted {
    color: var(--text-muted);
  }

  .empty {
    background: var(--bg-card);
    border: 1px solid var(--divider);
    border-radius: var(--radius-card);
    padding: var(--space-6);
    text-align: center;
  }
  .empty h3 {
    margin: 0 0 var(--space-2);
    color: var(--text-primary);
  }
  .empty p {
    margin: 0;
  }

  .persist-error {
    color: var(--accent-error);
    padding: var(--space-2) var(--space-3);
  }

  .undo-bar {
    width: 100%;
    min-height: 44px; /* wet-thumb touch target (D-08) */
    padding: var(--space-5) var(--space-4);
    background: var(--bg-raised);
    color: var(--text-primary);
    border: 0;
    border-radius: var(--radius-button);
    font: var(--type-title-subtitle);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  /* --- Relabel palette --- */
  .palette-scrim {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 40;
  }
  .palette {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 41;
    background: var(--bg-card);
    border-top-left-radius: var(--radius-card);
    border-top-right-radius: var(--radius-card);
    padding: var(--space-5) var(--space-4) calc(var(--space-6) + env(safe-area-inset-bottom));
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }
  .palette-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: var(--text-primary);
  }
  .palette-close {
    background: transparent;
    border: 0;
    color: var(--text-muted);
    font-size: 1.1rem;
    cursor: pointer;
    min-width: 44px;
    min-height: 44px;
  }
  .chip-grid {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
  }
  .chip {
    min-height: 44px; /* wet-thumb (D-11) */
    padding: var(--space-2) var(--space-3);
    background: var(--bg-raised);
    border: 1px solid var(--divider);
    border-radius: var(--radius-button);
    cursor: pointer;
    display: flex;
    align-items: center;
    -webkit-tap-highlight-color: transparent;
  }
  .recall {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .recall-input {
    width: 100%;
    min-height: 44px;
    padding: var(--space-2) var(--space-3);
    background: var(--bg-raised);
    border: 1px solid var(--divider);
    border-radius: var(--radius-button);
    color: var(--text-primary);
    font-size: 16px; /* >=16px prevents iOS focus-zoom (app.css:214) */
  }
  .recall-list {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
  }
  .recall-item {
    min-height: 44px;
    padding: var(--space-2) var(--space-3);
    background: var(--bg-raised);
    border: 1px solid var(--divider);
    border-radius: var(--radius-button);
    color: var(--text-primary);
    font: var(--type-body);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
</style>
